/**
 * Separating the author's own note from the statement it sits in front of.
 *
 * `normalizeSql` collapses every whitespace run to one space before storing the
 * preview, so a `--` comment arrives with no newline to end it and runs straight
 * into the SQL. Left alone it takes the whole width of a table cell: four rows
 * that begin `-- Anchor the trailing window on the table-wide MAX(date_start)`
 * read as the same statement even though their SQL diverges past the cut.
 *
 * The boundary is therefore not recoverable exactly. It is found by looking for
 * the first word that can open a statement, which is where the newline was. A
 * comment whose prose contains one of those words — `-- rebuild the SELECT list`
 * — splits early, which is why the full preview stays on the cell's hover and in
 * the drawer: this decides what is read first, never what is available.
 */

/**
 * Words that can begin a BigQuery statement or a script's first step. `WITH`
 * counts because a CTE chain opens with it, and `DECLARE` and `BEGIN` because a
 * procedural script's first line is routinely one of them.
 */
const STATEMENT_OPENERS = [
  "SELECT",
  "WITH",
  "INSERT",
  "UPDATE",
  "DELETE",
  "MERGE",
  "CREATE",
  "REPLACE",
  "DECLARE",
  "BEGIN",
  "CALL",
  "EXECUTE",
  "EXPORT",
  "TRUNCATE",
  "DROP",
  "ALTER",
  "GRANT",
  "REVOKE",
  "ASSERT",
  "SET",
] as const;

const OPENER_PATTERN = new RegExp(`\\b(?:${STATEMENT_OPENERS.join("|")})\\b`, "i");

/** `--` and `#` both open a line comment in BigQuery. */
const LINE_COMMENT = /^(?:--|#)/;

const BLOCK_OPEN = "/*";
const BLOCK_CLOSE = "*/";

/** Between two comment runs, since the newline that separated them is gone. */
const NOTE_SEPARATOR = " · ";

export interface SqlNoteSplit {
  /** The leading comments, markers removed, or null when there were none. */
  note: string | null;
  /**
   * The statement itself, on one line. Empty when the preview's 200-character
   * cap landed inside the comment, so no SQL was stored at all.
   */
  statement: string;
}

export function splitSqlNote(sqlPreview: string): SqlNoteSplit {
  let rest = sqlPreview.replace(/\s+/g, " ").trim();
  const notes: string[] = [];

  for (;;) {
    if (rest.startsWith(BLOCK_OPEN)) {
      // A block comment carries its own terminator, so this one is exact.
      const closesAt = rest.indexOf(BLOCK_CLOSE, BLOCK_OPEN.length);
      notes.push(
        closesAt === -1 ? rest.slice(BLOCK_OPEN.length) : rest.slice(BLOCK_OPEN.length, closesAt),
      );
      rest = closesAt === -1 ? "" : rest.slice(closesAt + BLOCK_CLOSE.length).trim();
      continue;
    }

    if (LINE_COMMENT.test(rest)) {
      const opensAt = rest.search(OPENER_PATTERN);
      notes.push(opensAt === -1 ? rest : rest.slice(0, opensAt));
      rest = opensAt === -1 ? "" : rest.slice(opensAt).trim();
      continue;
    }

    break;
  }

  return { note: cleanNote(notes), statement: rest };
}

/**
 * The markers are chrome once the note is displayed as one, and the runs they
 * separated are what the author wrote on separate lines — so they are joined
 * back with a separator rather than run together into one sentence.
 */
function cleanNote(notes: string[]): string | null {
  const parts = notes
    .join(" ")
    .split(/--|#/)
    .map((part) => part.trim())
    .filter((part) => part.length > 0);

  return parts.length === 0 ? null : parts.join(NOTE_SEPARATOR);
}
